import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../types';
import { userService } from '../../services/user';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  sessionId: string | null;
  requiresTFA: boolean;
  loading: boolean;
  error: string | null;
  lastCheckIn: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  sessionId: localStorage.getItem('sessionId'),
  requiresTFA: false,
  loading: false,
  error: null,
  lastCheckIn: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{
      user: User;
      accessToken: string;
      refreshToken: string;
      sessionId: string;
    }>) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.sessionId = action.payload.sessionId;
      state.requiresTFA = false;
      state.loading = false;
      state.error = null;
      state.lastCheckIn = new Date().toISOString();

      // Persist tokens
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
      localStorage.setItem('sessionId', action.payload.sessionId);
    },
    loginRequiresTFA: (state, action: PayloadAction<{ sessionId: string }>) => {
      state.requiresTFA = true;
      state.sessionId = action.payload.sessionId;
      state.loading = false;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.sessionId = null;
      state.requiresTFA = false;
      state.error = null;

      // Clear persisted tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionId');
      
      // Clear user cache
      userService.clearCache();
    },
    updateTokens: (state, action: PayloadAction<{
      accessToken: string;
      refreshToken?: string;
    }>) => {
      state.accessToken = action.payload.accessToken;
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken;
        localStorage.setItem('refreshToken', action.payload.refreshToken);
      }
      localStorage.setItem('accessToken', action.payload.accessToken);
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setLastCheckIn: (state, action: PayloadAction<string>) => {
      state.lastCheckIn = action.payload;
    }
  }
});

export const {
  loginStart,
  loginSuccess,
  loginRequiresTFA,
  loginFailure,
  logout,
  updateTokens,
  setUser,
  setLastCheckIn
} = authSlice.actions;

export default authSlice.reducer;